import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { Section, Spinner } from './ui';

/**
 * Reusable cross-entity association widget (design/023). Renders a tab row
 * (one tab per target kind) with chips of currently-associated items + a
 * typeahead search + Add/Remove.
 *
 * @param {object} props
 * @param {'course'|'practice'|'group'|'event'} props.sourceKind
 * @param {string} props.sourceId  — UUID of the source entity
 * @param {Array<'course'|'practice'|'group'|'event'>} [props.targetKinds]
 *        — defaults to the other three kinds
 * @param {string} [props.title]   — Section header (default "Associated")
 */
const KIND_LABEL = {
  course: 'Courses', practice: 'Practices', group: 'Groups', event: 'Events',
};

export default function AssociationTabs({
  sourceKind, sourceId,
  targetKinds,
  title = 'Associated',
}) {
  const kinds = useMemo(
    () => (targetKinds && targetKinds.length
      ? targetKinds
      : ['course', 'practice', 'group', 'event'].filter((k) => k !== sourceKind)),
    [sourceKind, targetKinds],
  );
  const [tab, setTab] = useState(kinds[0]);

  return (
    <Section title={title}>
      <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: 10 }}>
        Discovery links only — associating doesn't grant access or membership.
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, borderBottom: '1px solid var(--border)' }}>
        {kinds.map((k) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`btn btn-sm ${tab === k ? 'btn-primary' : ''}`}
            style={{ borderRadius: '8px 8px 0 0', marginBottom: -1 }}
          >{KIND_LABEL[k]}</button>
        ))}
      </div>
      <AssociationPane sourceKind={sourceKind} sourceId={sourceId} targetKind={tab} />
    </Section>
  );
}

/**
 * One tab's contents. Chips row + typeahead. Loads on mount + whenever the
 * targetKind changes.
 */
function AssociationPane({ sourceKind, sourceId, targetKind }) {
  const [items, setItems] = useState(null);           // AssocRef[] currently associated
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await api.listAssociated(sourceKind, sourceId, targetKind);
      setItems(Array.isArray(list) ? list : []);
    } catch (e) { alert(e.message); setItems([]); }
  }, [sourceKind, sourceId, targetKind]);
  useEffect(() => { setItems(null); setQ(''); setResults([]); load(); }, [load]);

  // Debounced search.
  useEffect(() => {
    const t = setTimeout(async () => {
      const needle = q.trim();
      if (!needle) { setResults([]); return; }
      setSearching(true);
      try {
        const list = await api.searchAssociations(targetKind, needle);
        // Hide already-associated + self.
        const associatedIds = new Set((items || []).map((x) => x.id));
        setResults((Array.isArray(list) ? list : []).filter((r) => !associatedIds.has(r.id) && r.id !== sourceId));
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, targetKind, items, sourceId]);

  const add = async (ref) => {
    setBusy(true);
    try {
      await api.associate(sourceKind, sourceId, targetKind, ref.id);
      setQ(''); setResults([]);
      await load();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };
  const remove = async (ref) => {
    setBusy(true);
    try {
      await api.disassociate(sourceKind, sourceId, targetKind, ref.id);
      await load();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  if (items == null) return <Spinner />;

  return (
    <>
      {items.length === 0
        ? <div style={{ fontSize: '0.8125rem', color: 'var(--muted)', marginBottom: 12 }}>
            No {KIND_LABEL[targetKind].toLowerCase()} associated yet.
          </div>
        : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {items.map((r) => (
              <span key={r.id} className="pill" style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: '#eef2ff', color: '#3730a3', padding: '4px 8px 4px 10px',
              }}>
                {r.label || '(untitled)'}
                <button
                  className="btn btn-sm"
                  disabled={busy}
                  style={{ padding: '0 6px', background: 'transparent', color: '#3730a3', border: 'none', fontSize: '0.875rem' }}
                  onClick={() => remove(r)}
                  title="Remove"
                >✕</button>
              </span>
            ))}
          </div>
        )
      }

      <div style={{ position: 'relative', maxWidth: 480 }}>
        <input
          className="input"
          placeholder={`Search ${KIND_LABEL[targetKind].toLowerCase()} to associate…`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {q.trim() && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
            background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8,
            marginTop: 4, maxHeight: 260, overflow: 'auto', boxShadow: 'var(--shadow-card)',
          }}>
            {searching ? (
              <div style={{ padding: 10, fontSize: '0.8125rem', color: 'var(--muted)' }}>Searching…</div>
            ) : results.length === 0 ? (
              <div style={{ padding: 10, fontSize: '0.8125rem', color: 'var(--muted)' }}>No matches.</div>
            ) : results.map((r) => (
              <button
                key={r.id}
                onClick={() => add(r)}
                disabled={busy}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '8px 12px', background: 'transparent', border: 'none',
                  cursor: 'pointer', fontSize: '0.875rem',
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <b>{r.label || '(untitled)'}</b>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
